import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { css } from '@emotion/react';
import { ClipLoader } from 'react-spinners';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaEye } from 'react-icons/fa'; // For the view icon
import AdminActions from './AdminActions';

ChartJS.register(ArcElement, Tooltip, Legend);

const override = css`
  display: block;
  margin: 0 auto;
`;

const ApplicationTable = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState(null); // State for enlarged image
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3008/api/v1/';

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const response = await fetch(`${apiUrl}application/allApplication`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const data = await response.json();
        if (response.ok) {
          setApplications(data);
        } else {
          toast.error(data.message || 'Failed to fetch applications');
        }
      } catch (error) {
        console.error('Fetch Error:', error);
        toast.error('An error occurred while fetching applications');
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, [apiUrl]);

  const filteredApplications = applications.filter(app =>
    `${app.companyId?.companyName || ''} ${app.applicant[0]?.firstName || ''} ${app.applicant[0]?.lastName || ''} ${app.position || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pie chart data for status distribution
  const statusCounts = applications.reduce((acc, app) => {
    acc[app.status] = (acc[app.status] || 0) + 1;
    return acc;
  }, {});
  const pieData = {
    labels: Object.keys(statusCounts),
    datasets: [{
      data: Object.values(statusCounts),
      backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
      hoverOffset: 4,
    }],
  };

  // Handle image view
  const handleViewImage = (imageUrl) => {
    setSelectedImage(imageUrl);
    // Open in new tab for larger view (alternative: modal can be implemented)
    window.open(imageUrl, '_blank');
  };

  // Close image view (if using modal, uncomment and implement modal logic)
  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <ClipLoader color="#16F96DFF" css={override} size={50} />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-900 min-h-screen text-white">
      <h1 className="text-3xl font-bold mb-6 text-green-500">Applications - {applications.length > 0 ? applications.length : 0}</h1>
      <div className="mb-6">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by employer, applicant, or position..."
          className="p-3 border border-gray-700 rounded-md w-full md:w-1/3 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 overflow-x-auto shadow-lg rounded-lg">
          <table className="w-full bg-gray-800 border-collapse">
            <thead>
              <tr className="bg-gray-700">
                <th className="border-b border-gray-600 p-4 text-left text-green-400">Employer Name</th>
                <th className="border-b border-gray-600 p-4 text-left text-green-400">Applicant Name</th>
                <th className="border-b border-gray-600 p-4 text-left text-green-400">Position</th>
                <th className="border-b border-gray-600 p-4 text-left text-green-400">Company Address</th>
                <th className="border-b border-gray-600 p-4 text-left text-green-400">Availability</th>
                <th className="border-b border-gray-600 p-4 text-left text-green-400">Resume</th>
                <th className="border-b border-gray-600 p-4 text-left text-green-400">Status</th>
                <th className="border-b border-gray-600 p-4 text-left text-green-400">Date Hired</th>
                <th className="border-b border-gray-600 p-4 text-left text-green-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.map((app) => (
                <tr key={app._id} className="border-b border-gray-600 hover:bg-gray-700 transition-colors">
                  <td className="border-r border-gray-600 p-4">{app.companyId?.companyName || 'Unknown Employer'}</td>
                  <td className="border-r border-gray-600 p-4">{`${app.applicant[0]?.firstName || ''} ${app.applicant[0]?.lastName || ''}` || 'Unknown Applicant'}</td>
                  <td className="border-r border-gray-600 p-4">{app.position}</td>
                  <td className="border-r border-gray-600 p-4">{app.companyAddress}</td>
                  <td className="border-r border-gray-600 p-4">{app.availability}</td>
                  <td className="border-r border-gray-600 p-4 flex items-center">
                    {app.resume && (
                      <img
                        src={app.resume}
                        alt="Resume"
                        className="w-16 h-16 object-cover mr-2"
                      />
                    )}
                    {app.resume && (
                      <FaEye
                        className="text-green-500 cursor-pointer hover:text-green-300"
                        onClick={() => handleViewImage(app.resume)}
                      />
                    )}
                  </td>
                  <td className="border-r border-gray-600 p-4">{app.status}</td>
                  <td className="border-r border-gray-600 p-4">{app.dateHired || 'N/A'}</td>
                  <td className="border-r border-gray-600 p-4">
                    {localStorage.getItem('userType') === 'admin' && (
                      <AdminActions applicationId={app._id} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="lg:col-span-1 bg-gray-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold text-green-500 mb-4">Application Status Distribution</h2>
          <Pie data={pieData} />
        </div>
      </div>

      {/* Optional Modal for Image View (uncomment and implement if preferred) */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative">
            <img src={selectedImage} alt="Enlarged Resume" className="max-w-full max-h-[80vh]" />
            <button onClick={handleCloseImage} className="absolute top-2 right-2 text-white text-2xl">&times;</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationTable;